'use client'

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { Pin, Plus, Search } from 'lucide-react'
import type { Account, ChatMessage, Contact, ConversationSummary } from '@/types'
import { apiDelete, apiGet, apiPost } from '@/lib/api'
import { displayName, filterContacts, formatMessageTime } from '@/lib/utils'
import { LazyAvatar } from '@/components/LazyAvatar'
import { APP_MESSAGE_EVENT } from '@/lib/account-message-stream'
import { dialog } from '@/components/StandardDialog'
import { useContactMap } from '@/lib/contact-map'

const CONVERSATION_RECONCILE_INTERVAL_MS = 5 * 60_000

type ChatListProps = {
  contacts: Contact[]
  account: Account | null
  activeConversationWxid: string
  unreadCounts: Record<string, number>
  onConversationRead: (wxid: string) => void
  onSelect: (wxid: string) => void
}

type ConversationRow = {
  conversationWxid: string
  name: string
  avatar: string
  lastContent: string
  lastDirection: 'in' | 'out'
  lastMessageAt: number
  messageCount: number
  unreadCount: number
  pinned: boolean
  pinnedAt: number
}

type ContextMenuState = {
  visible: boolean
  x: number
  y: number
  row: ConversationRow | null
}

function sortRows(a: ConversationRow, b: ConversationRow) {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
  if (a.pinned && b.pinned && a.pinnedAt !== b.pinnedAt) return b.pinnedAt - a.pinnedAt
  if (b.lastMessageAt !== a.lastMessageAt) return b.lastMessageAt - a.lastMessageAt
  return a.name.localeCompare(b.name)
}

export function ChatList({
  contacts,
  account,
  activeConversationWxid,
  unreadCounts,
  onConversationRead,
  onSelect,
}: ChatListProps) {
  const [query, setQuery] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [hydratedGroups, setHydratedGroups] = useState<Record<string, Contact>>({})
  const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    row: null,
  })
  const fetchedRef = useRef<Set<string>>(new Set())
  // Only an account switch or unmount invalidates a group-info request; an
  // inbound message must not, or the reply is dropped with the wxids still
  // marked as fetched.
  const groupInfoGenerationRef = useRef(0)
  // 当前账号快照，供异步回调判断响应是否已过期（账号被切换）。
  const accountWxidRef = useRef(account?.wxid)

  const contactMap = useContactMap(contacts)

  const resolveContact = useCallback(
    (wxid: string): Contact | undefined => contactMap.get(wxid) || hydratedGroups[wxid],
    [contactMap, hydratedGroups],
  )

  const fetchConversations = useCallback(async () => {
    if (!account) {
      setConversations([])
      return
    }
    const requestWxid = account.wxid
    try {
      const response = await apiGet<{
        success: boolean
        conversations: ConversationSummary[]
      }>('/bot/conversations?wxid=' + encodeURIComponent(requestWxid))
      if (accountWxidRef.current !== requestWxid) return
      setConversations(response.conversations || [])
    } catch {
      // 刷新失败不清列表，保留上次结果。
    }
  }, [account])

  // 会话、群名/头像都是按微信号取的，账号一变就必须丢弃：留着的话新账号拉取失败时
  // 界面仍显示上一个账号的会话，点进去会带着新 wxid 往旧联系人发消息。
  useEffect(() => {
    fetchedRef.current = new Set()
    accountWxidRef.current = account?.wxid
    groupInfoGenerationRef.current += 1
    setConversations((current) => (current.length ? [] : current))
    setHydratedGroups((current) => (Object.keys(current).length ? {} : current))
    setContextMenu((current) => (current.visible ? { visible: false, x: 0, y: 0, row: null } : current))
    return () => {
      groupInfoGenerationRef.current += 1
    }
  }, [account?.wxid])

  useEffect(() => {
    if (!account) {
      setConversations([])
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const response = await apiGet<{
          success: boolean
          conversations: ConversationSummary[]
        }>('/bot/conversations?wxid=' + encodeURIComponent(account.wxid), { silent: true })
        if (!cancelled) setConversations(response.conversations || [])
      } catch {
        // 周期 reconcile 失败不清列表，保留上次结果等下一轮自愈。
      }
    }
    void load()
    const reconcile = () => {
      if (document.hidden) return
      void load()
    }
    const timer = window.setInterval(reconcile, CONVERSATION_RECONCILE_INTERVAL_MS)
    document.addEventListener('visibilitychange', reconcile)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', reconcile)
    }
  }, [account])

  useEffect(() => {
    if (!account) return
    const missing = conversations
      .map((conversation) => conversation.conversationWxid)
      .filter(
        (wxid) =>
          wxid.endsWith('@chatroom') && !contactMap.has(wxid) && !hydratedGroups[wxid] && !fetchedRef.current.has(wxid),
      )
    if (!missing.length) return
    missing.forEach((wxid) => fetchedRef.current.add(wxid))
    const generation = groupInfoGenerationRef.current
    // Un-mark so the next reconcile retries; leaving them marked would
    // pin these rows to the raw chatroom id for the whole page lifetime.
    const unmark = () => {
      if (groupInfoGenerationRef.current !== generation) return
      for (const wxid of missing) fetchedRef.current.delete(wxid)
    }
    void (async () => {
      try {
        const response = await apiPost<{
          success: boolean
          contacts?: Contact[]
          message?: string
        }>('/bot/group-info', { wxid: account.wxid, qids: missing })
        if (groupInfoGenerationRef.current !== generation) return
        if (!response.success) {
          unmark()
          return
        }
        if (response.contacts?.length) {
          setHydratedGroups((current) => {
            const next = { ...current }
            for (const contact of response.contacts!) {
              next[contact.wxid] = contact
            }
            return next
          })
        }
      } catch (error) {
        console.warn('group-info fetch failed', error)
        unmark()
      }
    })()
  }, [account, conversations, contactMap, hydratedGroups])

  useEffect(() => {
    const onMessage = (event: Event) => {
      const message = (event as CustomEvent<ChatMessage>).detail
      if (!account || message.accountWxid !== account.wxid) return
      setConversations((current) => {
        const previous = current.find((item) => item.conversationWxid === message.conversationWxid)
        const next = current.filter((item) => item.conversationWxid !== message.conversationWxid)
        next.unshift({
          conversationWxid: message.conversationWxid,
          lastContent: message.content,
          lastDirection: message.direction,
          lastMessageAt: message.createdAt,
          messageCount: (previous?.messageCount || 0) + 1,
          pinned: previous?.pinned ?? false,
          pinnedAt: previous?.pinnedAt,
        })
        return next
      })
    }
    window.addEventListener(APP_MESSAGE_EVENT, onMessage)
    return () => {
      window.removeEventListener(APP_MESSAGE_EVENT, onMessage)
    }
  }, [account])

  useEffect(() => {
    if (!activeConversationWxid) return
    onConversationRead(activeConversationWxid)
  }, [activeConversationWxid, onConversationRead])

  useEffect(() => {
    if (!contextMenu.visible) return
    const close = () => setContextMenu({ visible: false, x: 0, y: 0, row: null })
    document.addEventListener('click', close)
    document.addEventListener('contextmenu', close)
    return () => {
      document.removeEventListener('click', close)
      document.removeEventListener('contextmenu', close)
    }
  }, [contextMenu.visible])

  const rows = useMemo(() => {
    const byWxid = new Map<string, ConversationRow>()
    for (const conversation of conversations) {
      const contact = resolveContact(conversation.conversationWxid)
      byWxid.set(conversation.conversationWxid, {
        conversationWxid: conversation.conversationWxid,
        name: contact ? displayName(contact) : conversation.conversationWxid,
        avatar: contact?.avatar || '',
        lastContent: conversation.lastContent,
        lastDirection: conversation.lastDirection,
        lastMessageAt: conversation.lastMessageAt,
        messageCount: conversation.messageCount,
        unreadCount: unreadCounts[conversation.conversationWxid] || 0,
        pinned: conversation.pinned === true,
        pinnedAt: conversation.pinnedAt ?? 0,
      })
    }

    if (query.trim()) {
      for (const contact of filterContacts(contacts, query)) {
        if (!byWxid.has(contact.wxid)) {
          byWxid.set(contact.wxid, {
            conversationWxid: contact.wxid,
            name: displayName(contact),
            avatar: contact.avatar || '',
            lastContent: '',
            lastDirection: 'in',
            lastMessageAt: 0,
            messageCount: 0,
            unreadCount: 0,
            pinned: false,
            pinnedAt: 0,
          })
        }
      }
    }

    if (activeConversationWxid && !byWxid.has(activeConversationWxid)) {
      const contact = resolveContact(activeConversationWxid)
      byWxid.set(activeConversationWxid, {
        conversationWxid: activeConversationWxid,
        name: contact ? displayName(contact) : activeConversationWxid,
        avatar: contact?.avatar || '',
        lastContent: '',
        lastDirection: 'in',
        lastMessageAt: 0,
        messageCount: 0,
        unreadCount: 0,
        pinned: false,
        pinnedAt: 0,
      })
    }

    const allRows = Array.from(byWxid.values()).sort(sortRows)
    if (!query.trim()) return allRows
    const keyword = query.trim().toLowerCase()
    return allRows.filter(
      (row) => row.name.toLowerCase().includes(keyword) || row.conversationWxid.toLowerCase().includes(keyword),
    )
  }, [activeConversationWxid, resolveContact, contacts, conversations, query, unreadCounts])

  function openContextMenu(event: MouseEvent, row: ConversationRow) {
    event.preventDefault()
    setContextMenu({ visible: true, x: event.clientX, y: event.clientY, row })
  }

  async function togglePinRow(row: ConversationRow) {
    if (!account) return
    const desiredPinned = !row.pinned
    setConversations((current) =>
      current.map((item) =>
        item.conversationWxid === row.conversationWxid
          ? {
              ...item,
              pinned: desiredPinned,
              pinnedAt: desiredPinned ? Date.now() : undefined,
            }
          : item,
      ),
    )
    try {
      await apiPost<{ success: boolean }>('/bot/conversations/pin', {
        wxid: account.wxid,
        conversationWxid: row.conversationWxid,
        pinned: desiredPinned,
      })
      await fetchConversations()
    } catch (error) {
      console.warn('pin failed', error)
      await fetchConversations()
    }
  }

  async function deleteRow(row: ConversationRow) {
    if (!account) return
    if (
      !(await dialog.confirm({
        title: '确定删除该会话？',
        message: '确定删除该会话的所有聊天记录吗？此操作不可撤销。',
        danger: true,
      }))
    )
      return
    try {
      await apiDelete<{ success: boolean }>(
        '/bot/conversations?wxid=' +
          encodeURIComponent(account.wxid) +
          '&conversationWxid=' +
          encodeURIComponent(row.conversationWxid),
      )
      setConversations((current) => current.filter((item) => item.conversationWxid !== row.conversationWxid))
      if (activeConversationWxid === row.conversationWxid) {
        onSelect('')
      }
    } catch (error) {
      console.warn('delete conversation failed', error)
    }
  }

  async function createConversation() {
    if (!account) {
      setAlertDialog({
        title: '无法新建会话',
        message: '请先登录并选择一个微信号。',
      })
      return
    }
    const keyword = query.trim()
    const candidate = keyword && rows.length === 1 ? rows[0].conversationWxid : keyword
    const wxid = (
      await dialog.prompt({ title: '新建会话', message: '请输入好友 wxid 或群聊 chatroom id', defaultValue: candidate })
    )?.trim()
    if (!wxid) return
    setConversations((current) => {
      if (current.some((item) => item.conversationWxid === wxid)) return current
      return [
        {
          conversationWxid: wxid,
          lastContent: '',
          lastDirection: 'in',
          lastMessageAt: 0,
          messageCount: 0,
          pinned: false,
        },
        ...current,
      ]
    })
    setQuery('')
    onConversationRead(wxid)
    onSelect(wxid)
  }

  const searchWrapperClass = [
    'flex-1 flex items-center gap-2 h-8 px-2 rounded-md bg-white border transition-colors',
    searchFocused ? 'border-accent ring-1 ring-accent/30' : 'border-hair',
  ].join(' ')

  return (
    <div className="flex flex-col h-full bg-sub border-r border-hair">
      <div className="h-[60px] px-3 flex items-center gap-2 border-b border-hair/60 shrink-0">
        <div className={searchWrapperClass}>
          <Search size={14} className="text-muted shrink-0" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="搜索"
            className="flex-1 bg-transparent text-sm text-ink placeholder:text-muted outline-none"
          />
        </div>
        <button
          type="button"
          onClick={createConversation}
          className="w-8 h-8 flex items-center justify-center rounded-md text-soft hover:bg-subHover"
          title="新建会话"
        >
          <Plus size={18} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {rows.length ? (
          rows.map((row) => {
            const active = row.conversationWxid === activeConversationWxid
            return (
              <button
                key={row.conversationWxid}
                type="button"
                onClick={() => onSelect(row.conversationWxid)}
                onContextMenu={(event) => openContextMenu(event, row)}
                className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors ${
                  active ? 'bg-subActive' : 'hover:bg-subHover'
                }`}
              >
                <span className="relative shrink-0">
                  <LazyAvatar
                    src={row.avatar}
                    seed={row.conversationWxid}
                    className="w-10 h-10 rounded object-cover bg-white"
                  />
                  {row.unreadCount > 0 ? (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
                      {row.unreadCount > 99 ? '99+' : row.unreadCount}
                    </span>
                  ) : null}
                </span>
                <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 min-w-0">
                      {row.pinned ? <Pin size={12} className="text-accent shrink-0" aria-label="已置顶" /> : null}
                      <strong className="truncate text-sm font-medium text-ink">{row.name}</strong>
                    </span>
                    <span className="text-[11px] text-muted shrink-0">
                      {row.lastMessageAt ? formatMessageTime(row.lastMessageAt) : ''}
                    </span>
                  </span>
                  <span className="text-xs text-muted truncate">
                    {row.lastContent
                      ? `${row.lastDirection === 'out' ? '我: ' : ''}${row.lastContent}`
                      : row.conversationWxid}
                  </span>
                </span>
              </button>
            )
          })
        ) : (
          <div className="h-full flex items-center justify-center text-muted text-sm">暂无会话</div>
        )}
      </div>
      {contextMenu.visible && contextMenu.row ? (
        <div
          className="fixed bg-white border border-hair shadow-card rounded py-1 z-overlay w-32 overflow-hidden text-sm"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => {
              const row = contextMenu.row!
              setContextMenu({ visible: false, x: 0, y: 0, row: null })
              void togglePinRow(row)
            }}
            className="w-full text-left px-3 py-2 text-ink hover:bg-page"
          >
            {contextMenu.row.pinned ? '取消置顶' : '置顶'}
          </button>
          <button
            type="button"
            onClick={() => {
              const row = contextMenu.row!
              setContextMenu({ visible: false, x: 0, y: 0, row: null })
              void deleteRow(row)
            }}
            className="w-full text-left px-3 py-2 text-red-500 hover:bg-red-50"
          >
            删除聊天
          </button>
        </div>
      ) : null}
      {alertDialog ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-list-alert-title"
          className="fixed inset-0 z-overlay bg-black/35 flex items-center justify-center px-4"
          onClick={() => setAlertDialog(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white border border-hair shadow-card p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="chat-list-alert-title" className="text-base font-semibold text-ink">
              {alertDialog.title}
            </h3>
            <p className="mt-3 text-sm leading-6 text-soft">{alertDialog.message}</p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="h-9 px-4 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-hover"
                onClick={() => setAlertDialog(null)}
              >
                知道了
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
